// ================= IMPORTS =================
import { lazy, Suspense, useEffect, useRef } from "react";
import { Navigate, Outlet, Route, Routes, useLocation, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  adminHasPermission,
  adminHasAnyPermission,
  adminHasAllPermissions,
} from "./utils/adminPermissions";
import {
  parentHasPermission,
  parentHasAnyPermission,
  parentHasAllPermissions,
} from "./utils/parentPermissions";
import {
  studentHasPermission,
  studentHasAnyPermission,
  studentHasAllPermissions,
} from "./utils/studentPermissions";
import { studentIsAllowMobile } from "./utils/studentMobileAccess";
import useStudentPortalMode from "./hooks/useStudentPortalMode";
import StudentSubscriptionLock from "./components/StudentSubscriptionLock";
import MobilePlan from "./pages/Student/MobilePlan";
import {
  tutorHasPermission,
  tutorHasAnyPermission,
  tutorHasAllPermissions,
} from "./utils/tutorPermissions";
import {
  testerHasPermission,
  testerHasAnyPermission,
  testerHasAllPermissions,
} from "./utils/testerPermissions";
import { Box, Typography, CircularProgress } from "@mui/material";
import { masterFormRoutePermissions } from "./constants/masterFormPermissions";

// Pages
import AdminLogin from "./pages/Adminlogin";
import TutorLogin from "./pages/TutorLogin";
import Course from "./pages/Course/Course";
import CourseView from "./pages/Course/CourseView";
import CourseAssignment from "./pages/Course/CourseAssignment";
import CourseAssignmentList from "./pages/Course/CourseAssignmentList";
import CourseContent from "./pages/Course/CourseContent";
import CourseList from "./pages/Course/CourseList";
import CourseReport from "./pages/Course/CourseReport";
import CourseTest from "./pages/Course/CourseTest";
import TestList from "./pages/Course/TestList";
import TopicSubtopicList from "./pages/Course/TopicList";
import TimeSlots from "./pages/Course/CourseTimeSlot";

import AdminLayout from "./pages/Layout/AdminLayout";
import TutorLayout from "./pages/Layout/TutorLayout";
import Layout from "./pages/Layout/Layout";
import ParentLayout from "./pages/Layout/ParentLayout";
import StudentLayout from "./pages/Layout/StudentLayout";

import Login from "./pages/Login";
import ParentSso from "./pages/ParentSso.jsx";
import StudentSso from "./pages/StudentSso.jsx";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import SignUp from "./pages/SignUp";
import Dashboard from "./pages/Student/Dashboard";
import AdminDashboard from "./pages/Dashboard/AdminDashboard";
import ResourceAllocation from "./pages/Course/ResourceAllocation";
import HolidayManagement from "./pages/Course/HolidayManagement";
import Testimonials from "./pages/Course/Testimonials";
import AnalyticsLayout from "./pages/Analytics/AnalyticsLayout";
import BatchCapacityAnalytics from "./pages/Analytics/BatchCapacityAnalytics";
import CourseContentAnalytics from "./pages/Analytics/CourseContentAnalytics";
import PaymentOverdueAnalytics from "./pages/Analytics/PaymentOverdueAnalytics";
import AcademicSupportAnalytics from "./pages/Analytics/AcademicSupportAnalytics";
import WeakTopicCohorts from "./pages/Analytics/WeakTopicCohorts";
import TargetSchoolsAnalytics from "./pages/Analytics/TargetSchoolsAnalytics";
import RevisionPlanningAnalytics from "./pages/Analytics/RevisionPlanningAnalytics";
import ConsortiumCoverage from "./pages/Analytics/ConsortiumCoverage";
import GeographicAnalytics from "./pages/Analytics/GeographicAnalytics";
import AppUsageAnalytics from "./pages/Analytics/AppUsageAnalytics";
import AiUsageAnalytics from "./pages/Analytics/AiUsageAnalytics";
import NotificationsAnalytics from "./pages/Analytics/NotificationsAnalytics";
import MarketingAnalytics from "./pages/Analytics/MarketingAnalytics";
import MarketingLayout from "./pages/Analytics/MarketingLayout";
import MarketingCompetitors from "./pages/Analytics/MarketingCompetitors";
import StudentDashboard from "./pages/Student/Dashboard";
import StudentAnnouncements from "./pages/Student/Announcements";
import StudentNotifications from "./pages/Student/Notifications";
import StudentMessages from "./pages/Student/Messages";
import StudentCertificates from "./pages/Student/Certificates";
import StudentClasses from "./pages/Student/Classes";
import StudentMeetingPage from "./pages/Student/StudentMeetingPage";
import StudentTest from "./pages/Student/StudentTest";
import MyCourseTest from "./pages/Student/Homework/MyCourseTest";
import MyCourseVideo from "./pages/Student/Video/MyCourseVideo";
import MyCourseView from "./pages/Student/Video/MyCourseView";
import MyCourseVideoPage from "./pages/MyCourseVideoPage";
import VideoLessonsPage from "./pages/VideoLessonsPage";

import SubTopicContentView from "./pages/Student/SubTopicContentView";
import SubTopicTest from "./pages/Student/SubTopicTest";
import TopicTest from "./pages/Student/TopicTest";
import TopicContentView from "./pages/Student/TopicContentView";
import Games from "./pages/Student/Games";
import GameLeaderboard from "./pages/Student/GameLeaderboard";
import FlashCards from "./pages/Student/FlashCards";
import StudentProfile from "./pages/Student/StudentProfile";
import WizardHub from "./pages/Student/Wizard/WizardHub";
import WizardAiTutoring from "./pages/Student/Wizard/WizardAiTutoring";
import WizardAiMarking from "./pages/Student/Wizard/WizardAiMarking";
import WizardWritingCoach from "./pages/Student/Wizard/WizardWritingCoach";
import WizardDailyWordChallenge from "./pages/Student/Wizard/WizardDailyWordChallenge";

import AiAnswerSheetUpload from "./pages/Student/AiAnswerSheetUpload";
import WorkSheetTake from "./pages/Student/WorkSheetTake";
import WorkSheetReview from "./pages/Student/WorkSheetReview.jsx";
import WorkSheetSweepHistory from "./pages/Student/WorkSheetSweepHistory.jsx";
import StudentPannel from "./pages/StudentPannel";

// Mock Exam
import MockExamCategory from "./pages/MockExam/MockExamCategory";
import MockExam from "./pages/MockExam/MockExam";
import PaperExtract from "./pages/MockExam/PaperExtract";

// Paper
import PaperCategory from "./pages/Paper/PaperCategory";

// Master Form
import MasterForm from "./pages/MasterForm/MasterForm";
import DisplayOrder from "./pages/DisplayOrder/DisplayOrder";
import CompetitiveDashboard from "./pages/CompetitiveIntel/CompetitiveDashboard";

// Admin
import FlashCardList from "./pages/Admin/FlashCards/FlashCardList";
import FlashCardForm from "./pages/Admin/FlashCards/FlashCardForm";
import GameTopicList from "./pages/Admin/Games/GameTopicList";
import GameTopicForm from "./pages/Admin/Games/GameTopicForm";
import GameTopicView from "./pages/Admin/Games/GameTopicView";
import GameContentList from "./pages/Admin/Games/GameContentList";
import GameContentForm from "./pages/Admin/Games/GameContentForm";
import GameContentView from "./pages/Admin/Games/GameContentView";
import ParentList from "./pages/Admin/ParentList";
import ParentBilling from "./pages/Admin/ParentBilling";
import TutorList from "./pages/Admin/TutorList";
import TutorForm from "./pages/Admin/TutorForm";
import AdminPaperPurchases from "./pages/Admin/PaperPurchases";
import TutorPaperAccessRequests from "./pages/Admin/TutorPaperAccessRequests";
import AdminBillingTransactions from "./pages/Admin/AdminBillingTransactions";
import SubscriptionPlans from "./pages/Billing/SubscriptionPlans";
import Announcements from "./pages/Admin/Announcements";
import Awards from "./pages/Admin/Awards";
import Certificates from "./pages/Admin/Certificates";
import ExamModeManagement from "./pages/Admin/ExamModeManagement";
import AdminSettings from "./pages/Admin/AdminSettings";

// Admin Student
import StudentList from "./pages/Admin/Student/StudentList";
import AuditTrailList from "./pages/Admin/AuditTrail/AuditTrailList";
import RolesManagement from "./pages/Admin/RolesManagement";
import PermissionsManagement from "./pages/Admin/PermissionsManagement";
import StudentForm from "./pages/Admin/Student/StudentForm";
import StudentDetails from "./pages/Admin/Student/StudentDetails";

// Parent
import Billing from "./pages/Parent/Billing/Billing";
import BillingInformation from "./pages/Parent/BillingInformation";
import ParentMyPurchases from "./pages/Parent/MyPurchases";
import ChildAssistantChats from "./pages/Parent/ChildAssistantChats";
import ChildClasses from "./pages/Parent/ChildClasses";
import AddStudent from "./pages/Parent/Student/AddStudent";
import EditStudent from "./pages/Parent/Student/EditStudent";
import ChangePassword from "./pages/Parent/Student/ChangePassword";
import MyStudentList from "./pages/Parent/Student/MyStudentList";
import ParentDashboard from "./pages/Parent/Dashboard";
import SchoolReadinessDetail from "./pages/Parent/SchoolReadinessDetail";
import ParentReadinessOverview from "./pages/Parent/ParentReadinessOverview";
import ParentAnnouncements from "./pages/Parent/Announcements";
import ParentNotifications from "./pages/Parent/Notifications";
import ParentMessages from "./pages/Parent/Messages";
import ParentCertificates from "./pages/Parent/Certificates";
import EndOfReport from "./pages/Parent/Progress/EndOfReport";
import FinishedTest from "./pages/Parent/Progress/FinishedTest";
import Setting from "./pages/Parent/Setting";
import CourseTargetArea from "./pages/Parent/Progress/CourseTargetArea";
import TestScores from "./pages/Parent/Progress/TestScores";
import ParentMockExamResults from "./pages/Parent/Progress/ParentMockExamResults.jsx";
import ParentPastPaperResults from "./pages/Parent/Progress/ParentPastPaperResults.jsx";

import MyCurrentCourseAssignment from "./pages/Student/MyCurrentCourseAssignment";

// Tutor
import TutorDashboard from "./pages/Tutor/TutorDashboard";
import TutorStudents from "./pages/Tutor/TutorStudents";
import TutorStudentResources from "./pages/Tutor/TutorStudentResources";
import TutorStudentResourceReview from "./pages/Tutor/TutorStudentResourceReview";
import TutorCourses from "./pages/Tutor/TutorCourses";
import TutorGroups from "./pages/Tutor/TutorGroups";
import GroupResources from "./pages/Student/GroupResources";
import ClassRecordings from "./pages/Student/ClassRecordings";
import TutorResources from "./pages/Tutor/TutorResources";
import TutorResourceReview from "./pages/Admin/TutorResourceReview";
import TutorTimeslots from "./pages/Tutor/TutorTimeslots";
import TutorAssignments from "./pages/Tutor/TutorAssignments";
import TutorTests from "./pages/Tutor/TutorTests";
import TutorMockExams from "./pages/Tutor/TutorMockExams";
import TutorPapers from "./pages/Tutor/TutorPapers";
import TutorAnnouncements from "./pages/Tutor/TutorAnnouncements";
import TutorNotifications from "./pages/Tutor/TutorNotifications";
import TutorAwards from "./pages/Tutor/TutorAwards";
import TutorCertificates from "./pages/Tutor/TutorCertificates";
import TutorStartClass from "./pages/Tutor/TutorStartClass";
import TutorClasses from "./pages/Tutor/TutorClasses";
import TutorScheduleClass from "./pages/Tutor/TutorScheduleClass";
import TutorRecordings from "./pages/Tutor/TutorRecordings";
import TutorMeetingPage from "./pages/Tutor/TutorMeetingPage";
import AdminLeads from "./pages/Admin/AdminLeads";
import CourseWaitingList from "./pages/Admin/CourseWaitingList";
import TutorApplications from "./pages/Admin/TutorApplications";
import WhatsAppCampaign from "./pages/Admin/WhatsAppCampaign.jsx";
import SupportChatInbox from "./pages/Admin/SupportChatInbox.jsx";
import AssistantKnowledgeBase from "./pages/Admin/AssistantKnowledgeBase";
import EmailSubscriptions from "./pages/Admin/EmailSubscriptions.jsx";
import TuitionRequests from "./pages/Admin/TuitionRequests";
import MobileRevisionSupportRequests from "./pages/Admin/MobileRevisionSupportRequests";
import MobileHelpSupport from "./pages/Admin/MobileHelpSupport";
import MobileAppIssues from "./pages/Admin/MobileAppIssues";
import AiPrompts from "./pages/Admin/AiPrompts";
import MobileUsers from "./pages/Admin/MobileUsers";
import MobileUserDetails from "./pages/Admin/MobileUserDetails";
import MobileUserProgress from "./pages/Admin/MobileUserProgress";

// 404
import NotFound from "./pages/NotFound";

// AI Assignment Creator
import AiAssignmentCreator from "./pages/Admin/AiAssignmentCreator";

// Question Bank
import QuestionBankList from "./pages/QuestionBank/QuestionBankList";
import ExplanationVideos from "./pages/ExplanationVideos/ExplanationVideos";
import QuestionBankForm from "./pages/QuestionBank/QuestionBankForm";
import QuestionBankImport from "./pages/QuestionBank/QuestionBankImport";
import QuestionBankImportReview from "./pages/QuestionBank/QuestionBankImportReview";
import QuestionBankPapers from "./pages/QuestionBank/QuestionBankPapers";
import SchoolPracticePapers from "./pages/QuestionBank/SchoolPracticePapers";
import QuestionBankPaperViewer from "./pages/QuestionBank/QuestionBankPaperViewer";
import QuestionBankRecentImports from "./pages/QuestionBank/QuestionBankRecentImports";
import FlaggedQuestions from "./pages/QuestionBank/FlaggedQuestions";
import SimilarPaperReview from "./pages/QuestionBank/SimilarPaperReview";
import SimilaritySummary from "./pages/QuestionBank/SimilaritySummary";
import McqReview from "./pages/QuestionBank/McqReview";
import VerificationDashboard from "./pages/Admin/VerificationDashboard";

// Student Mock Exams
import StudentMockExams from "./pages/Student/MockExams/StudentMockExams";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamReport from "./pages/Student/MockExams/StudentMockExamReport";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamExamReport from "./pages/Student/MockExams/StudentMockExamExamReport";
// eslint-disable-next-line no-unused-vars -- used in Route elements below
import StudentMockExamReview from "./pages/Student/MockExams/StudentMockExamReview";

import StudentPastPapers from "./pages/Student/PastPapers/StudentPastPapers";
import StudentPaperResult from "./pages/Student/PastPapers/StudentPaperResult";
import StudentPaperReview from "./pages/Student/PastPapers/StudentPaperReview";
import StudentPaperReport from "./pages/Student/PastPapers/StudentPaperReport";

const Paper = lazy(() => import("./pages/Paper/Paper"));
const StudentMockExamTake = lazy(() => import("./pages/Student/MockExams/StudentMockExamTake"));
const StudentPaperTake = lazy(() => import("./pages/Student/PastPapers/StudentPaperTake"));

// ============ AUTH GUARDS ============

/** API returns e.g. "Super Admin"; login stores `role.toLowerCase()` → "super admin". */
function normalizePortalRole(role) {
  if (role == null) return "";
  return String(role).toLowerCase().trim().replace(/\s+/g, " ");
}

/** Same admin SPA + Passport scope: Admin or Super Admin. */
function isAdminPortalRole(role) {
  const r = normalizePortalRole(role);
  return r === "admin" || r === "super admin" || r === "super_admin";
}

// Redirect via window.location to avoid "The operation is insecure" from history.push
const SafeRedirect = ({ to }) => {
  const location = useLocation();
  useEffect(() => {
    const targetPath = to.startsWith("/") ? to : `/${to}`;
    const currentPath = location.pathname.replace(/\/$/, "") || "/";
    const normalizedTarget = targetPath.replace(/\/$/, "") || "/";
    if (currentPath !== normalizedTarget) {
      window.location.replace(to);
    }
  }, [to, location.pathname]);
  return null;
};

// General Protected Route
const RequireAuth = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <SafeRedirect to="/login" />;
};

// Admin Protected Route
const RequireAdmin = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  return token && (isAdminPortalRole(role) || role === "tester")
    ? children
    : <SafeRedirect to="/login" />;
};

/** Old links to the Roles tab in Master Forms (?entity=roles) now open the Roles & Permissions section. */
const MasterFormsRolesRedirect = ({ children }) => {
  const [searchParams] = useSearchParams();
  if (searchParams.get("entity") === "roles") {
    return <Navigate to="/admin/roles" replace />;
  }
  return children;
};

/**
 * Block admin routes unless localStorage admin-permissions matches.
 * Use one of: `permission` (single), `anyOf` (needs one), `allOf` (needs all).
 */
const RequireAdminPermission = ({ permission, anyOf, allOf, children }) => {
  const isTester = localStorage.getItem("role") === "tester";
  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : adminHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : adminHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : adminHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to="/admin/course-list" replace />;
  }
  return children;
};

// Tutor Protected Route – redirect to tutor login (not main login)
const RequireTutor = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  return token && (role === "tutor" || role === "tester")
    ? children
    : <SafeRedirect to="/tutor-login" />;
};

// Role-Based Route (student, tutor, parent) — testers may access any portal
const RequireRole = ({ children, allowedRoles }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");
  if (!token) return <SafeRedirect to="/login" />;
  return (allowedRoles.includes(role) || role === "tester")
    ? children
    : <SafeRedirect to="/login" />;
};

// Guest Route - blocks access if already logged in
const GuestRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  if (token) {
    if (role === "tester" || isAdminPortalRole(role)) {
      return <SafeRedirect to="/admin/course-list" />;
    }
    switch (normalizePortalRole(role)) {
      case "student":
        return <SafeRedirect to="/student" />;
      case "parent":
        return <SafeRedirect to="/parent/" />;
      case "tutor":
        return <SafeRedirect to="/tutor" />;
      default:
        // Unknown/stale role: clear and show login to avoid redirect loop with "/"
        localStorage.removeItem("token");
        localStorage.removeItem("role");
        localStorage.removeItem("userData");
        return children;
    }
  }

  return children;
};


// Redirect users to their main dashboard
// On admin.11plusmagic.co.uk (REACT_APP_ADMIN_HOSTNAME) unauthenticated users go to /admin-login
// On online.11plusmagic.co.uk unauthenticated users go to /login
const PARENT_PREFIX = process.env.REACT_APP_PARENT_PREFIX || "parent";
const STUDENT_PREFIX = process.env.REACT_APP_STUDENT_PREFIX || "student";

/** Student index: dashboard when Settings → Student allows it, else first allowed section (RBAC). */
/**
 * Course-only pages (homework, videos, classes, announcements). A student with both a course
 * and a mobile plan who is on the Mobile side goes back to the dashboard, where the toggle is,
 * and so does a student who is not enrolled in any course.
 */
const CourseSideOnly = ({ children }) => {
  const { canToggleDashboard, dashboardView, hasCourseEnrollment, loading } = useStudentPortalMode();
  // No course at all: there is no homework, class or video to show, only a failing fetch.
  // Decided on the server's answer, never on a cached guess while student/settings loads.
  if ((!loading && !hasCourseEnrollment) || (canToggleDashboard && dashboardView === "mobile")) {
    return <Navigate to={`/${STUDENT_PREFIX}`} replace />;
  }
  return children;
};

const StudentHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : studentHasPermission;
  const checkAny  = isTester ? testerHasAnyPermission : studentHasAnyPermission;
  const { hideMobileOnlySections } = useStudentPortalMode();
  const { studentPermissionsSynced = true } = useOutletContext() ?? {};
  if (!isTester && !studentPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  const dashboardEnabled = localStorage.getItem("student-show-dashboard") !== "0";
  if (dashboardEnabled || checkPerm("student-portal-view-dashboard")) {
    return <StudentDashboard />;
  }
  const fallbacks = [
    { perm: "student-portal-homework-my-assignment", path: "my-current-assignment" },
    { perm: "student-portal-homework-assignment-results", path: "my-course-test" },
    { perm: "student-portal-video-my-course-video", path: "my-course-video" },
    { perm: "student-portal-video-my-video-views", path: "my-course-view" },
    { perm: "student-portal-worksheet", path: "ai-answer-sheet" },
    { perm: "student-portal-mock-exams", path: "mock-exams" },
    // Past papers: own permission, or mock exams (same cohort until DB/seeder assigns past-papers everywhere)
    {
      anyOf: ["student-portal-past-papers", "student-portal-mock-exams"],
      path: "past-papers",
    },
    { perm: "student-portal-flash-cards", path: "flash-cards" },
    { perm: "student-portal-flash-cards-quiz", path: "flash-cards?mode=quiz" },
    { perm: "student-portal-classes", path: "classes" },
    { perm: "student-portal-games-play", path: "games" },
    { perm: "student-portal-games-leaderboard", path: "games/leaderboard" },
    { perm: "student-portal-announcements", path: "announcements" },
    { perm: "student-portal-certificates", path: "certificates" },
    { perm: "student-portal-view-profile", path: "profile" },
  ];
  for (const entry of fallbacks) {
    if (entry.mobileOnly && hideMobileOnlySections) continue;
    const ok = entry.anyOf?.length
      ? checkAny(entry.anyOf)
      : checkPerm(entry.perm);
    if (ok) {
      return <Navigate to={`/${STUDENT_PREFIX}/${entry.path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any student portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

/** Tutor index: dashboard or first allowed section (RBAC). */
const TutorHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : tutorHasPermission;
  const { tutorPermissionsSynced = true } = useOutletContext() ?? {};
  if (!isTester && !tutorPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (checkPerm("tutor-portal-view-dashboard")) {
    return <TutorDashboard />;
  }
  const fallbacks = [
    { perm: "tutor-portal-students", path: "students" },
    { perm: "tutor-portal-courses", path: "courses" },
    { perm: "tutor-portal-groups", path: "groups" },
    { perm: "tutor-portal-resources", path: "resources" },
    { perm: "tutor-portal-classes", path: "classes" },
    { perm: "tutor-portal-mock-exams", path: "mock-exams" },
    { perm: "tutor-portal-papers", path: "papers" },
    { perm: "tutor-portal-timeslots", path: "timeslots" },
    { perm: "tutor-portal-assignments", path: "assignments" },
    { perm: "tutor-portal-tests", path: "tests" },
    { perm: "tutor-portal-announcements", path: "announcements" },
    { perm: "tutor-portal-awards", path: "awards" },
    { perm: "tutor-portal-certificates", path: "certificates" },
  ];
  for (const { perm, path } of fallbacks) {
    if (checkPerm(perm)) {
      return <Navigate to={`/tutor/${path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any tutor portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

/** Parent index: dashboard or first section the user may access (RBAC). */
const ParentHomeGate = () => {
  const isTester = localStorage.getItem("role") === "tester";
  const checkPerm = isTester ? testerHasPermission : parentHasPermission;
  const { parentPermissionsSynced = true } = useOutletContext() ?? {};
  if (!isTester && !parentPermissionsSynced) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 240,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }
  if (checkPerm("parent-panel-view-dashboard")) {
    return <ParentDashboard />;
  }
  const fallbacks = [
    { perm: "parent-panel-view-my-students", path: "my-student-list" },
    { perm: "parent-panel-manage-students", path: "my-student-list" },
    { perm: "parent-panel-change-student-password", path: "change-password" },
    { perm: "parent-panel-view-end-of-report", path: "end-of-report" },
    { perm: "parent-panel-view-course-target-area", path: "course-target-area" },
    { perm: "parent-panel-view-finished-test", path: "finished-test" },
    { perm: "parent-panel-view-test-scores", path: "test-scores" },
    { perm: "parent-panel-view-ai-answer-sheet", path: "ai-answer-sheet" },
    { perm: "parent-panel-view-billing", path: "billing/all" },
    { perm: "parent-panel-view-announcements", path: "announcements" },
    { perm: "parent-panel-view-certificates", path: "certificates" },
    { perm: "parent-panel-manage-billing-information", path: "billing-information" },
    { perm: "parent-panel-account-settings", path: "settings" },
  ];
  for (const { perm, path } of fallbacks) {
    if (checkPerm(perm)) {
      return <Navigate to={`/${PARENT_PREFIX}/${path}`} replace />;
    }
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography>
        You do not have access to any parent portal sections. Contact your administrator.
      </Typography>
    </Box>
  );
};

const RequireParentPermission = ({
  permission,
  anyOf,
  allOf,
  allowBypassWhenMustChangePassword,
  children,
}) => {
  const isTester = localStorage.getItem("role") === "tester";
  const mustChange =
    typeof window !== "undefined" &&
    localStorage.getItem("mustChangePassword") === "1";
  const ok = (() => {
    if (
      allowBypassWhenMustChangePassword &&
      mustChange &&
      permission === "parent-panel-account-settings"
    ) {
      return true;
    }
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : parentHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : parentHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : parentHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to={`/${PARENT_PREFIX}`} replace />;
  }
  return children;
};

const RequireStudentPermission = ({
  permission,
  anyOf,
  allOf,
  children,
}) => {
  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const isTester = role === "tester";

  // Permissions alone are not enough to decide this: switching student -> parent
  // leaves `student-permissions` in localStorage, and an absent list falls open,
  // so a parent session would keep rendering the student portal while every API
  // call carries the parent's token. Student-only actions then fail confusingly
  // (the PIN switch returns "Only students can switch to the parent portal").
  // An empty role means a legacy session, which is left alone.
  const wrongPortal = Boolean(role) && role !== "student" && !isTester;

  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : studentHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : studentHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : studentHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!wrongPortal && !ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok, wrongPortal]);
  if (wrongPortal) {
    return <Navigate to={`/${PARENT_PREFIX}`} replace />;
  }
  if (!ok) {
    return <Navigate to={`/${STUDENT_PREFIX}`} replace />;
  }
  return children;
};


/** Wizard tools — only for students registered as mobile users (is_allow_mobile). */
const RequireMobileStudent = ({ children }) => {
  const { canToggleDashboard, dashboardView } = useStudentPortalMode();
  // A course + plan student on the Course side has no Wizard (it is part of the mobile plan).
  const ok = studentIsAllowMobile() && !(canToggleDashboard && dashboardView === "course");
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("Wizard is only available for mobile app students.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to={`/${STUDENT_PREFIX}`} replace />;
  }
  return children;
};

/**
 * The Mobile Plan tab, and where Stripe returns after a plan checkout.
 *
 * A student who has bought a course, mock exam or past paper keeps working in the portal, so
 * they get the Mobile Plan page — an offer, not a barrier. A student with nothing bought has
 * no portal to use, so they get the full-screen lock instead.
 */
/**
 * "?plans=1" (a "Choose a plan" button) or a plan checkout return opens the plan list
 * directly; anything else — the post-login landing above all — asks first whether to just
 * buy items or renew the plan.
 */
const choosePlanStep = (search) => {
  const params = new URLSearchParams(search || "");
  return params.get("plans") === "1" || params.has("session_id") ? "plan" : "choose";
};

const StudentChoosePlanGate = () => {
  const location = useLocation();
  const { subscriptionLock, subscriptionLockResolved, subscriptionLockKnown, hasCourseEnrollment } = useStudentPortalMode();

  // Login lands here with storage just cleared, so the lock is unknown for a moment. Deciding
  // then read "unlocked" and bounced every lapsed student to the dashboard before the
  // question could be asked.
  if (!subscriptionLockResolved || !subscriptionLockKnown) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // A running trial stays on the dashboard until it ends. The Choose a plan button
  // (?plans=1) is the only way onto the list before then. A Stripe return must stay
  // here too, or a plan they started during the trial never confirms.
  const trialParams = new URLSearchParams(location.search || "");
  if (subscriptionLock?.isTrial && !subscriptionLock?.catalogueLocked) {
    if (trialParams.get("plans") === "1" || trialParams.has("session_id")) {
      return <MobilePlan lock={subscriptionLock} />;
    }
    return <Navigate to="/student" replace />;
  }

  // A website student who never had a plan (no course, not locked) reaches this page from the
  // no-plan dashboard's "See plans". Show the plan page rather than bouncing them back; a
  // Stripe return must land here too, or the plan they just bought never confirms.
  if (!subscriptionLock?.locked && !hasCourseEnrollment) {
    if (trialParams.get("plans") === "1" || trialParams.has("session_id")) {
      return <MobilePlan lock={subscriptionLock} />;
    }
  }

  if (!subscriptionLock?.locked) {
    return <Navigate to="/student" replace />;
  }

  // Asked once per lapse: after "Just buy", go straight to the dashboard; after "Renew",
  // straight to the plans. A "Choose a plan" button (?plans=1) always opens the plans.
  // A declined card, or the trip back from Stripe's card form (?billing_return=1), is a repair,
  // not a choice: the lock screen shows "Update payment details", and on the return trip it
  // calls student/subscription/recover, which charges the new card and restarts the plan.
  // Nothing else restarts it, so neither may be short-circuited to the dashboard below.
  const billingReturn = new URLSearchParams(location.search || "").get("billing_return") === "1";
  if (subscriptionLock.recoveryAction === "payment_method_update" || billingReturn) {
    return (
      <StudentSubscriptionLock
        lock={subscriptionLock}
        title="Choose a plan to continue"
        initialStep="plan"
      />
    );
  }

  // Free trial ended with no plan: there is no "just buy items" path and no dashboard to go
  // back to, so the plan list is the only screen (MobileSubscriptionAccessService::trialEndedLockFor).
  if (subscriptionLock.catalogueLocked === true) {
    return (
      <StudentSubscriptionLock
        lock={subscriptionLock}
        title="Your free trial has ended"
        initialStep="plan"
      />
    );
  }

  let step = choosePlanStep(location.search);
  // Already bought mock exams, past papers, a pack or a course (`worksheetsLocked === false`,
  // MobileSubscriptionAccessService::hasIndividualPurchases): they are a buyer, so no
  // question — straight to the dashboard, where the "Choose a plan" banner stays on offer.
  if (step === "choose" && subscriptionLock.worksheetsLocked === false) {
    return <Navigate to="/student" replace />;
  }
  if (step === "choose" && subscriptionLock.lapsedChoice === "items") {
    return <Navigate to="/student" replace />;
  }
  if (step === "choose" && subscriptionLock.lapsedChoice === "plan") {
    step = "plan";
  }

  // One screen for both tiers. This used to branch to StudentMobilePlan for students who
  // had bought something, which meant two plan pickers to keep in step — and they had
  // already drifted (different badges, different rules about the free trial).
  return (
    <StudentSubscriptionLock
      lock={subscriptionLock}
      title="Choose a plan to continue"
      initialStep={step}
    />
  );
};

/**
 * Learning sections a lapsed mobile-plan student cannot open. The same block is enforced by
 * EnsureStudentSubscriptionAccess on the API, so this only decides what the student sees.
 *
 * `scope`:
 *  - "plan" (default): class resources — need a running plan or free trial.
 *  - "worksheets": also open to a lapsed paid plan that bought a course / mock exam / paper.
 *  - "extras" (flash cards, games): also open to a lapsed plan or an ended free trial that
 *    bought a course / mock exam / past or practice paper / pack.
 *  - "wizard": same rule as "extras" — a buyer keeps it.
 *  - "open": never locked (the portal home). Only a plan checkout return shows the lock
 *    screen, which confirms the payment.
 */
const RequireSubscription = ({ children, scope = "plan" }) => {
  const location = useLocation();
  const { subscriptionLock, subscriptionLockResolved } = useStudentPortalMode();

  if (!subscriptionLockResolved) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Same answers the API gives in EnsureStudentSubscriptionAccess for each scope.
  const scopeLocked = {
    worksheets: subscriptionLock?.worksheetsLocked !== false,
    extras: subscriptionLock?.extrasLocked !== false,
    wizard: subscriptionLock?.wizardLocked !== false,
  }[scope];
  const locked = subscriptionLock?.locked === true && scopeLocked !== false;

  if (subscriptionLock?.locked && scope === "open") {
    const params = new URLSearchParams(location.search || "");
    // A plan checkout returns with only ?session_id; a mock exam / past paper purchase adds
    // ?purchase=success and must not be handed to the plan fulfilment in the lock screen.
    if (params.has("session_id") && !params.has("purchase")) {
      return (
        <StudentSubscriptionLock lock={subscriptionLock} title="Choose a plan to continue" initialStep="plan" />
      );
    }
    // An ended free trial has no dashboard: the plan screen is where they land.
    if (subscriptionLock.catalogueLocked === true) {
      return <Navigate to="/student/choose-plan" replace />;
    }
    return children;
  }

  if (locked) {
    const onChoosePlan = location.pathname.replace(/\/+$/, "").endsWith("/choose-plan");
    const hasCheckoutReturn = new URLSearchParams(location.search || "").has("session_id");
    if (!onChoosePlan && !hasCheckoutReturn) {
      return (
        <Navigate
          to={`/student/choose-plan${location.search || ""}`}
          replace
          state={{ from: location.pathname }}
        />
      );
    }
    return (
      <StudentSubscriptionLock
        lock={subscriptionLock}
        title="Choose a plan to continue"
        initialStep={choosePlanStep(location.search)}
      />
    );
  }

  return children;
};

const RequireTutorPermission = ({
  permission,
  anyOf,
  allOf,
  children,
}) => {
  const isTester = localStorage.getItem("role") === "tester";
  const ok = (() => {
    if (Array.isArray(anyOf) && anyOf.length > 0) {
      return isTester ? testerHasAnyPermission(anyOf) : tutorHasAnyPermission(anyOf);
    }
    if (Array.isArray(allOf) && allOf.length > 0) {
      return isTester ? testerHasAllPermissions(allOf) : tutorHasAllPermissions(allOf);
    }
    return isTester ? testerHasPermission(permission) : tutorHasPermission(permission);
  })();
  const toastShown = useRef(false);
  useEffect(() => {
    if (!ok && !toastShown.current) {
      toast.error("You do not have permission to access this page.");
      toastShown.current = true;
    }
  }, [ok]);
  if (!ok) {
    return <Navigate to="/tutor" replace />;
  }
  return children;
};

const RedirectByRole = () => {
  const role = localStorage.getItem("role");
  const token = localStorage.getItem("token");
  const isAdminHost = window.location.hostname === process.env.REACT_APP_ADMIN_HOSTNAME;
  const defaultLogin = isAdminHost ? "/admin-login" : "/login";
  if (!token) return <SafeRedirect to={defaultLogin} />;
  if (role === "tester" || isAdminPortalRole(role)) {
    return <SafeRedirect to="/admin/course-list" />;
  }
  switch (normalizePortalRole(role)) {
    case "student":
      return <SafeRedirect to="/student" />;
    case "parent":
      return <SafeRedirect to="/parent" />;
    case "tutor":
      return <SafeRedirect to="/tutor" />;
    default:
      return <SafeRedirect to={defaultLogin} />;
  }
};

const AppRoutes = () => {
  return (
    <Suspense fallback={
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
        <CircularProgress size={40} />
      </Box>
    }>
    <Routes>

      {/* PUBLIC ROUTES */}
      <Route path="/sign-up" element={
        <GuestRoute>
          <SignUp />
        </GuestRoute>
      } />
      <Route path="/parent-sso" element={<ParentSso />} />
      <Route path="/student-sso" element={<StudentSso />} />
      <Route path="/login" element={
        <GuestRoute>
          <Login />
        </GuestRoute>
      } />
      <Route path="/admin-login" element={
        <GuestRoute>
          <AdminLogin />
        </GuestRoute>
      } />
      <Route path="/tutor-login" element={
        <GuestRoute>
          <TutorLogin />
        </GuestRoute>
      } />
      <Route path="/forgot-password" element={
        <GuestRoute>
          <ForgotPassword />
        </GuestRoute>
      } />
      <Route path="/reset-password" element={
        <GuestRoute>
          <ResetPassword />
        </GuestRoute>
      } />

      {/* Escalation emails sent before the fix linked to /support-chat (no /admin prefix). */}
      <Route path="/support-chat" element={<Navigate to="/admin/support-chat" replace />} />

      {/* ADMIN ROUTES */}
      <Route path="/admin" element={
        <RequireAdmin>
          <AdminLayout />
        </RequireAdmin>
      }>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="resource-allocation" element={<ResourceAllocation />} />
        <Route path="holiday-management" element={<HolidayManagement />} />
        <Route path="testimonials" element={<Testimonials />} />
        <Route
          path="analytics"
          element={
            <RequireAdminPermission permission="view-analytics">
              <AnalyticsLayout />
            </RequireAdminPermission>
          }
        >
          <Route index element={<Navigate to="batch-capacity" replace />} />
          <Route path="batch-capacity" element={<BatchCapacityAnalytics />} />
          <Route path="course-content" element={<CourseContentAnalytics />} />
          <Route path="payment-overdue" element={<PaymentOverdueAnalytics />} />
          <Route path="academic-support" element={<AcademicSupportAnalytics />} />
          <Route path="weak-topics" element={<WeakTopicCohorts />} />
          <Route path="target-schools" element={<TargetSchoolsAnalytics />} />
          <Route path="revision-planning" element={<RevisionPlanningAnalytics />} />
          <Route path="coverage" element={<ConsortiumCoverage />} />
          <Route path="geographic" element={<GeographicAnalytics />} />
          <Route path="app-usage" element={<AppUsageAnalytics />} />
          <Route path="ai-usage" element={<AiUsageAnalytics />} />
          <Route path="notifications" element={<NotificationsAnalytics />} />
          <Route path="marketing" element={<MarketingLayout />}>
            <Route index element={<MarketingAnalytics />} />
            <Route path="competitors" element={<MarketingCompetitors />} />
          </Route>
        </Route>
        {/* Create vs edit: sidebar already splits permissions; routes must match so deep links respect RBAC */}
        <Route
          path="tutor-resources"
          element={
            <RequireAdminPermission permission="verify-tutor_resource">
              <TutorResourceReview />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course/:id/edit"
          element={
            <RequireAdminPermission permission="update-courses">
              <Course />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course/:id"
          element={
            <RequireAdminPermission permission="view-courses">
              <CourseView />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course"
          element={
            <RequireAdminPermission permission="create-courses">
              <Course />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-assignment/:id"
          element={
            <RequireAdminPermission anyOf={["update-course-durations", "update-assignments"]}>
              <CourseAssignment />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-assignment"
          element={
            <RequireAdminPermission anyOf={["create-course-durations", "create-assignments"]}>
              <CourseAssignment />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-content/:type?/:id?"
          element={
            <RequireAdminPermission anyOf={["view-course-topics", "create-course-topics", "update-course-topics", "delete-course-topics"]}>
              <CourseContent />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-test/:id"
          element={
            <RequireAdminPermission permission="update-assignments">
              <CourseTest />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-test"
          element={
            <RequireAdminPermission anyOf={["create-assignments", "use-ai-assignment-creator"]}>
              <CourseTest />
            </RequireAdminPermission>
          }
        />
        <Route
          path="course-list"
          element={
            <RequireAdminPermission permission="view-courses">
              <CourseList />
            </RequireAdminPermission>
          }
        />
        <Route
          path="timeslot"
          element={
            <RequireAdminPermission anyOf={["view-course-timeslots", "create-course-timeslots", "update-course-timeslots", "delete-course-timeslots"]}>
              <TimeSlots />
            </RequireAdminPermission>
          }
        />
        <Route
          path="assignment-list"
          element={
            <RequireAdminPermission anyOf={["view-course-durations", "view-assignments", "create-assignments", "update-assignments", "delete-assignments"]}>
              <CourseAssignmentList />
            </RequireAdminPermission>
          }
        />
        <Route
          path="topic/subtopic-list"
          element={
            <RequireAdminPermission anyOf={["view-course-topics", "create-course-topics", "update-course-topics", "delete-course-topics"]}>
              <TopicSubtopicList />
            </RequireAdminPermission>
          }
        />
        <Route path="test-list" element={
          <RequireAdminPermission anyOf={["view-assignments", "create-assignments", "update-assignments", "delete-assignments", "use-ai-assignment-creator"]}>
            <TestList />
          </RequireAdminPermission>
        } />
        <Route
          path="course-report"
          element={
            <RequireAdminPermission permission="view-course-reports">
              <CourseReport />
            </RequireAdminPermission>
          }
        />
        <Route
          path="exam-modes"
          element={
            <RequireAdminPermission permission="manage-exam-modes">
              <ExamModeManagement />
            </RequireAdminPermission>
          }
        />
        <Route
          path="settings"
          element={
            <RequireAdminPermission permission="manage-exam-modes">
              <AdminSettings />
            </RequireAdminPermission>
          }
        />
        <Route path="mock-exam-categories" element={
          <RequireAdminPermission anyOf={["view-mock-exam-categories", "create-mock-exam-categories", "update-mock-exam-categories", "delete-mock-exam-categories"]}>
            <MockExamCategory />
          </RequireAdminPermission>
        } />
        <Route path="paper-categories" element={
          <RequireAdminPermission anyOf={["view-paper-categories", "create-paper-categories", "update-paper-categories", "delete-paper-categories"]}>
            <PaperCategory />
          </RequireAdminPermission>
        } />
        <Route
          path="mock-exams"
          element={
            <RequireAdminPermission
              anyOf={[
                "view-mock-exams",
                "create-mock-exams",
                "update-mock-exams",
                "delete-mock-exams",
              ]}
            >
              <MockExam />
            </RequireAdminPermission>
          }
        />
        <Route path="mock-exam/mock/:mockExamId/report" element={
          <RequireAdminPermission anyOf={["view-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamExamReport />
          </RequireAdminPermission>
        } />
        <Route path="mock-exam/exam/:mockExamId/report" element={
          <RequireAdminPermission anyOf={["view-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamExamReport />
          </RequireAdminPermission>
        } />
        <Route path="mock-exam/result/:attemptId/report" element={
          <RequireAdminPermission anyOf={["view-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamReport />
          </RequireAdminPermission>
        } />
        <Route path="mock-exam/result/:attemptId/review" element={
          <RequireAdminPermission anyOf={["view-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamReview />
          </RequireAdminPermission>
        } />
        <Route path="mock-exams/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList forceBillingView billingType="mock" />
          </RequireAdminPermission>
        } />
        <Route path="mock-exams/paper-extract" element={
          <RequireAdminPermission anyOf={["view-paper-extracts", "create-paper-extracts", "update-paper-extracts", "delete-paper-extracts"]}>
            <PaperExtract />
          </RequireAdminPermission>
        } />
        {/* <Route path="paper-categories" element={<PaperCategory />} /> */}
        <Route path="papers" element={
          <RequireAdminPermission anyOf={["view-papers", "create-papers", "update-papers", "delete-papers"]}>
            <Paper />
          </RequireAdminPermission>
        } />
        <Route path="papers/billing" element={
          <RequireAdminPermission permission="view-paper-purchases">
            <AdminPaperPurchases />
          </RequireAdminPermission>
        } />
        <Route path="tutor-paper-requests" element={
          <RequireAdminPermission permission="manage-tutor-paper-access">
            <TutorPaperAccessRequests />
          </RequireAdminPermission>
        } />
        <Route path="billing-transactions" element={
          <RequireAdminPermission anyOf={["view-parents-subscriptions", "view-mobile-users"]}>
            <AdminBillingTransactions />
          </RequireAdminPermission>
        } />
        <Route path="subscription-plans" element={
          <RequireAdminPermission anyOf={["view-parents-subscriptions", "view-mobile-users"]}>
            <SubscriptionPlans />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <MobileUsers />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users/:id" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <MobileUserDetails />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users/:id/progress" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <MobileUserProgress />
          </RequireAdminPermission>
        } />
        <Route path="mobile-users/:id/worksheet/:testId/attempt/:attemptId" element={
          <RequireAdminPermission anyOf={["view-mobile-users", "view-parents-subscriptions"]}>
            <WorkSheetReview />
          </RequireAdminPermission>
        } />
        <Route path="master-forms" element={
          <MasterFormsRolesRedirect>
            <RequireAdminPermission anyOf={masterFormRoutePermissions()}>
              <MasterForm />
            </RequireAdminPermission>
          </MasterFormsRolesRedirect>
        } />
        <Route path="display-order" element={
          <RequireAdminPermission anyOf={masterFormRoutePermissions()}>
            <DisplayOrder />
          </RequireAdminPermission>
        } />
        <Route path="competitive-intel" element={
          <RequireAdminPermission anyOf={["view-analytics", "view-master-form-schools", "update-master-form-schools"]}>
            <CompetitiveDashboard />
          </RequireAdminPermission>
        } />

        {/* Admin Student Management Routes */}
        <Route path="students" element={
          <RequireAdminPermission permission="view-students">
            <StudentList />
          </RequireAdminPermission>
        } />
        <Route path="student/add" element={
          <RequireAdminPermission permission="create-students">
            <StudentForm />
          </RequireAdminPermission>
        } />
        <Route path="student/:id/edit" element={
          <RequireAdminPermission permission="update-students">
            <StudentForm />
          </RequireAdminPermission>
        } />
        <Route path="student/:id" element={
          <RequireAdminPermission permission="view-students">
            <StudentDetails />
          </RequireAdminPermission>
        } />

        {/* Admin Tutor Management Routes */}
        <Route path="tutors" element={
          <RequireAdminPermission permission="view-tutors">
            <TutorList />
          </RequireAdminPermission>
        } />
        <Route path="tutor/add" element={
          <RequireAdminPermission permission="update-tutors">
            <TutorForm />
          </RequireAdminPermission>
        } />
        <Route path="tutor/edit/:id" element={
          <RequireAdminPermission permission="update-tutors">
            <TutorForm />
          </RequireAdminPermission>
        } />

        {/* Admin Parent Management Routes */}
        <Route path="parents" element={
          <RequireAdminPermission permission="view-parents">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="course/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentList />
          </RequireAdminPermission>
        } />
        <Route path="parent/:parentId/billing" element={
          <RequireAdminPermission permission="view-parents-subscriptions">
            <ParentBilling />
          </RequireAdminPermission>
        } />

        {/* AI Assignment Creator */}
        <Route path="ai-assignment-creator" element={
          <RequireAdminPermission anyOf={["use-ai-assignment-creator", "create-assignments", "update-assignments"]}>
            <AiAssignmentCreator />
          </RequireAdminPermission>
        } />


        {/* Question Bank */}
        <Route path="explanation-videos" element={
          <RequireAdminPermission anyOf={["view-question-bank", "view-assignments", "update-assignments"]}>
            <ExplanationVideos />
          </RequireAdminPermission>
        } />
        <Route path="question-bank" element={
          <RequireAdminPermission permission="view-question-bank">
            <QuestionBankList />
          </RequireAdminPermission>
        } />
        <Route path="flagged-questions" element={
          <RequireAdminPermission permission="view-flagged-questions">
            <FlaggedQuestions />
          </RequireAdminPermission>
        } />
        <Route path="verification-dashboard" element={
          <RequireAdminPermission anyOf={["view-verification-dashboard", "verify-explanation_video"]}>
            <VerificationDashboard />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/papers" element={
          <RequireAdminPermission permission="view-question-bank-papers">
            <QuestionBankPapers />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/papers/:id" element={
          <RequireAdminPermission permission="view-question-bank-papers">
            <QuestionBankPaperViewer />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/school-practice-papers" element={
          <RequireAdminPermission permission="view-question-bank">
            <SchoolPracticePapers />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/recent-imports" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <QuestionBankRecentImports />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/create" element={
          <RequireAdminPermission permission="create-question-bank">
            <QuestionBankForm />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/:id/edit" element={
          <RequireAdminPermission permission="update-question-bank">
            <QuestionBankForm />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/import" element={
          <RequireAdminPermission anyOf={["view-question-bank-imports", "create-question-bank-imports"]}>
            <QuestionBankImport />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/import/:id/review" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <QuestionBankImportReview />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/similar-papers" element={
          <RequireAdminPermission anyOf={["view-question-bank-similar-papers", "update-question-bank-similar-papers"]}>
            <SimilarPaperReview />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/similarity-summary" element={
          <RequireAdminPermission anyOf={["view-mock-exams", "view-papers", "create-papers", "update-papers", "delete-papers"]}>
            <SimilaritySummary />
          </RequireAdminPermission>
        } />
        <Route path="question-bank/mcq-review" element={
          <RequireAdminPermission permission="view-question-bank-imports">
            <McqReview />
          </RequireAdminPermission>
        } />

        {/* Admin Announcements Routes */}
        <Route path="announcements" element={
          <RequireAdminPermission permission="view-announcements">
            <Announcements />
          </RequireAdminPermission>
        } />

        {/* Admin Flash Cards Routes */}
        <Route path="flash-cards" element={
          <RequireAdminPermission permission="view-flash-cards">
            <FlashCardList />
          </RequireAdminPermission>
        } />
        <Route path="flash-cards/add" element={
          <RequireAdminPermission permission="create-flash-cards">
            <FlashCardForm />
          </RequireAdminPermission>
        } />
        <Route path="flash-cards/:id/edit" element={
          <RequireAdminPermission permission="update-flash-cards">
            <FlashCardForm />
          </RequireAdminPermission>
        } />

        {/* Admin Games — Game topics */}
        <Route path="game-topics" element={
          <RequireAdminPermission permission="view-game-topics">
            <GameTopicList />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/add" element={
          <RequireAdminPermission permission="create-game-topics">
            <GameTopicForm />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/:id/edit" element={
          <RequireAdminPermission permission="update-game-topics">
            <GameTopicForm />
          </RequireAdminPermission>
        } />
        <Route path="game-topics/:id" element={
          <RequireAdminPermission anyOf={["view-game-topics", "update-game-topics"]}>
            <GameTopicView />
          </RequireAdminPermission>
        } />

        {/* Admin Games — Game contents */}
        <Route path="game-contents" element={
          <RequireAdminPermission permission="view-game-contents">
            <GameContentList />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/add" element={
          <RequireAdminPermission permission="create-game-contents">
            <GameContentForm />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/:id/edit" element={
          <RequireAdminPermission permission="update-game-contents">
            <GameContentForm />
          </RequireAdminPermission>
        } />
        <Route path="game-contents/:id" element={
          <RequireAdminPermission anyOf={["view-game-contents", "update-game-contents"]}>
            <GameContentView />
          </RequireAdminPermission>
        } />

        {/* Admin Awards Routes */}
        <Route path="awards" element={
          <RequireAdminPermission permission="view-awards">
            <Awards />
          </RequireAdminPermission>
        } />

        {/* Admin Certificates Routes */}
        <Route path="certificates" element={
          <RequireAdminPermission permission="view-certificates">
            <Certificates />
          </RequireAdminPermission>
        } />

        {/* Admin Leads Routes */}
        <Route path="leads" element={
          <RequireAdminPermission permission="view-leads">
            <AdminLeads />
          </RequireAdminPermission>
        } />
        <Route path="tuition-requests" element={
          <RequireAdminPermission permission="view-leads">
            <TuitionRequests />
          </RequireAdminPermission>
        } />
        <Route path="course-waiting-list" element={
          <RequireAdminPermission permission="view-leads">
            <CourseWaitingList />
          </RequireAdminPermission>
        } />
        <Route path="tutor-applications" element={
          <RequireAdminPermission permission="view-leads">
            <TutorApplications />
          </RequireAdminPermission>
        } />
        <Route path="mobile-revision-support-requests" element={
          <RequireAdminPermission permission="view-leads">
            <MobileRevisionSupportRequests />
          </RequireAdminPermission>
        } />
        <Route path="mobile-help-support" element={
          <RequireAdminPermission anyOf={["view-leads", "view-support-chat"]}>
            <MobileHelpSupport />
          </RequireAdminPermission>
        } />
        <Route path="mobile-app-issues" element={
          <RequireAdminPermission anyOf={["view-leads", "view-support-chat"]}>
            <MobileAppIssues />
          </RequireAdminPermission>
        } />
        <Route path="ai-prompts" element={
          isAdminPortalRole(localStorage.getItem("role"))
            ? <AiPrompts />
            : <Navigate to="/admin/course-list" replace />
        } />
        <Route path="whatsapp" element={
          <RequireAdminPermission permission="send-whatsapp-msg">
            <WhatsAppCampaign />
          </RequireAdminPermission>
        } />

        {/* Support Chat (admin inbox) */}
        <Route
          path="support-chat/:conversationId?"
          element={
            <RequireAdminPermission permission="view-support-chat">
              <SupportChatInbox />
            </RequireAdminPermission>
          }
        />
        <Route
          path="assistant-knowledge-base"
          element={
            <RequireAdminPermission permission="view-support-chat">
              <AssistantKnowledgeBase />
            </RequireAdminPermission>
          }
        />

        {/* Roles & Permissions (needs the master switch plus view access) */}
        <Route
          path="roles"
          element={
            <RequireAdminPermission allOf={["manage-roles-permissions", "view-roles"]}>
              <RolesManagement />
            </RequireAdminPermission>
          }
        />
        <Route
          path="permissions"
          element={
            <RequireAdminPermission allOf={["manage-roles-permissions", "view-permissions"]}>
              <PermissionsManagement />
            </RequireAdminPermission>
          }
        />
        {/* Audit Trail (user activity log) */}
        <Route
          path="audit-trail"
          element={
            <RequireAdminPermission permission="view-audit-trail">
              <AuditTrailList />
            </RequireAdminPermission>
          }
        />

        {/* Email Subscriptions */}
        <Route
          path="subscriptions"
          element={
            <RequireAdminPermission permission="view-leads">
              <EmailSubscriptions />
            </RequireAdminPermission>
          }
        />
      </Route>

      {/* STUDENT ROUTES */}
      {/* Meeting only — no sidebar; hang up closes tab / returns to Classes (not close3.html) */}
      <Route
        path="/student/meeting/:roomCode"
        element={
          <RequireRole allowedRoles={["student"]}>
            <RequireStudentPermission permission="student-portal-classes">
              <StudentMeetingPage />
            </RequireStudentPermission>
          </RequireRole>
        }
      />
      <Route path="student" element={
        <RequireRole allowedRoles={["student"]}>
          <StudentLayout />
        </RequireRole>
      }>
        <Route index element={
          <RequireSubscription scope="open">
            <StudentHomeGate />
          </RequireSubscription>
        } />
        {/* Unpaid students land here to pick a plan / pay before opening learning sections. */}
        <Route
          path="choose-plan"
          element={
            <StudentChoosePlanGate />
          }
        />
        <Route path="classes" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-classes">
              <StudentClasses />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="announcements" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-announcements">
              <StudentAnnouncements />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="notifications" element={<StudentNotifications />} />
        <Route path="messages" element={<StudentMessages />} />
        <Route path="certificates" element={
          <RequireStudentPermission permission="student-portal-certificates">
            <StudentCertificates />
          </RequireStudentPermission>
        } />
        <Route path="my-course-test" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-homework-assignment-results">
              <MyCourseTest />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="course-target-area" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <MyStudentList />
          </RequireStudentPermission>
        } />
        <Route path="test-scores" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <TestScores />
          </RequireStudentPermission>
        } />
        <Route path="finished-test" element={
          <RequireStudentPermission permission="student-portal-homework-assignment-results">
            <FinishedTest />
          </RequireStudentPermission>
        } />

        <Route path="videos" element={
          <RequireStudentPermission
            anyOf={["student-portal-video-my-course-video", "student-portal-video-my-video-views"]}
          >
            <VideoLessonsPage />
          </RequireStudentPermission>
        } />
        <Route path="topic/content/view/:topic_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <TopicContentView />
          </RequireStudentPermission>
        } />
        <Route path="topic/test/:test_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <TopicTest />
          </RequireStudentPermission>
        } />
        <Route path="subtopic/content/view/:sub_topic_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <SubTopicContentView />
          </RequireStudentPermission>
        } />
        <Route path="subtopic/test/:sub_topic_test_id" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <SubTopicTest />
          </RequireStudentPermission>
        } />
        <Route path="my-course-video-page" element={
          <RequireStudentPermission permission="student-portal-video-my-course-video">
            <MyCourseVideoPage />
          </RequireStudentPermission>
        } />
        {/* Mock exams and past papers are open without an app plan: they can be bought one at a
            time, so a student whose plan ended keeps what they bought and can buy more. The API
            decides access per item. */}
        <Route path="mock-exams" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExams />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/exam/:mockExamId/report" element={
          <RequireStudentPermission permission="student-portal-mock-exams">
            <StudentMockExamExamReport />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/mock/:mockExamId/report" element={
          <RequireStudentPermission anyOf={["student-portal-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamExamReport />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/result/:attemptId/report" element={
          <RequireStudentPermission anyOf={["student-portal-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamReport />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/result/:attemptId/review" element={
          <RequireStudentPermission anyOf={["student-portal-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamReview />
          </RequireStudentPermission>
        } />
        <Route path="mock-exam/:id/take" element={
          <RequireStudentPermission anyOf={["student-portal-mock-exams", "verify-mock_exam"]}>
            <StudentMockExamTake />
          </RequireStudentPermission>
        } />
        <Route path="past-papers" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPastPapers />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/take" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperTake />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/result" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperResult />
          </RequireStudentPermission>
        } />
        <Route path="past-paper/:purchaseId/review" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperReview />
          </RequireStudentPermission>
        } />
        <Route path="past-paper-report/:paperId" element={
          <RequireStudentPermission
            anyOf={["student-portal-past-papers", "student-portal-mock-exams", "verify-paper"]}
          >
            <StudentPaperReport />
          </RequireStudentPermission>
        } />

        <Route element={<RequireSubscription scope="worksheets"><Outlet /></RequireSubscription>}>
          <Route path="ai-answer-sheet" element={
            <RequireStudentPermission anyOf={["student-portal-worksheet", "verify-worksheet"]}>
              <AiAnswerSheetUpload />
            </RequireStudentPermission>
          } />
          <Route path="work-sheet/:id/take" element={
            <RequireStudentPermission anyOf={["student-portal-worksheet", "verify-worksheet"]}>
              <WorkSheetTake />
            </RequireStudentPermission>
          } />
          <Route path="work-sheet/:testId/attempt/:attemptId/review" element={<WorkSheetReview />} />
          <Route path="work-sheet/:testId/sweep-history" element={<WorkSheetSweepHistory />} />
        </Route>

        <Route path="add-student" element={
          <RequireStudentPermission permission="student-portal-view-dashboard">
            <AddStudent />
          </RequireStudentPermission>
        } />
        <Route path="my-current-assignment" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-homework-my-assignment">
              <MyCurrentCourseAssignment />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="test" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-homework-my-assignment">
              <StudentTest />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="my-course-video" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-video-my-course-video">
              <MyCourseVideo />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />
        <Route path="my-course-view" element={
          <CourseSideOnly>
            <RequireStudentPermission permission="student-portal-video-my-video-views">
              <MyCourseView />
            </RequireStudentPermission>
          </CourseSideOnly>
        } />

        <Route element={<RequireSubscription><Outlet /></RequireSubscription>}>
          <Route path="class-resources" element={
            <CourseSideOnly>
              <RequireStudentPermission permission="student-portal-classes">
                <GroupResources />
              </RequireStudentPermission>
            </CourseSideOnly>
          } />
          <Route path="class-recordings" element={
            <CourseSideOnly>
              <RequireStudentPermission permission="student-portal-classes">
                <ClassRecordings />
              </RequireStudentPermission>
            </CourseSideOnly>
          } />
        </Route>

        {/* Same rule as flash cards and games: a buyer keeps it (wizardLockedFor). */}
        <Route element={<RequireSubscription scope="wizard"><Outlet /></RequireSubscription>}>
          <Route path="wizard" element={
            <RequireMobileStudent>
              <WizardHub />
            </RequireMobileStudent>
          } />
          <Route path="wizard/ai-tutoring" element={
            <RequireMobileStudent>
              <WizardAiTutoring />
            </RequireMobileStudent>
          } />
          <Route path="wizard/ai-marking" element={
            <RequireMobileStudent>
              <WizardAiMarking />
            </RequireMobileStudent>
          } />
          <Route path="wizard/writing-coach" element={
            <RequireMobileStudent>
              <WizardWritingCoach />
            </RequireMobileStudent>
          } />
          <Route path="wizard/daily-word-challenge" element={
            <RequireMobileStudent>
              <WizardDailyWordChallenge />
            </RequireMobileStudent>
          } />
        </Route>

        {/* Games and flash cards: a student who paid for a course, mock exam, past or
            practice paper, or pack keeps them when the plan lapses or the free trial ends
            (extrasLockedFor). Matches student.php. A sibling of the plan group, not a
            child — nested, the plan guard redirected first. */}
        <Route element={<RequireSubscription scope="extras"><Outlet /></RequireSubscription>}>
          <Route path="games" element={
            <RequireStudentPermission permission="student-portal-games-play">
              <Games />
            </RequireStudentPermission>
          } />
          <Route path="games/leaderboard" element={
            <RequireStudentPermission permission="student-portal-games-leaderboard">
              <GameLeaderboard />
            </RequireStudentPermission>
          } />
          <Route path="games/:subjectKey" element={
            <RequireStudentPermission permission="student-portal-games-play">
              <Games />
            </RequireStudentPermission>
          } />
          <Route path="flash-cards" element={
            <RequireStudentPermission
              anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
            >
              <FlashCards />
            </RequireStudentPermission>
          } />
          <Route path="flash-cards/:subjectKey" element={
            <RequireStudentPermission
              anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
            >
              <FlashCards />
            </RequireStudentPermission>
          } />
          <Route path="vocabulary/:subjectKey" element={
            <RequireStudentPermission
              anyOf={["student-portal-flash-cards", "student-portal-flash-cards-quiz"]}
            >
              <FlashCards />
            </RequireStudentPermission>
          } />
        </Route>
        <Route path="profile" element={
          <RequireStudentPermission permission="student-portal-view-profile">
            <StudentProfile />
          </RequireStudentPermission>
        } />
      </Route>

      {/* PARENT ROUTES */}
      <Route path="/parent" element={
        <RequireRole allowedRoles={["parent"]}>
          <ParentLayout />
        </RequireRole>
      }>
        <Route index element={<ParentHomeGate />} />
        <Route path="readiness" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-end-of-report"]}
          >
            <ParentReadinessOverview />
          </RequireParentPermission>
        } />
        <Route path="readiness/:studentId/:targetSchoolId" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-end-of-report"]}
          >
            <SchoolReadinessDetail />
          </RequireParentPermission>
        } />
        <Route path="announcements" element={
          <RequireParentPermission permission="parent-panel-view-announcements">
            <ParentAnnouncements />
          </RequireParentPermission>
        } />
        <Route path="notifications" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-announcements"]}
          >
            <ParentNotifications />
          </RequireParentPermission>
        } />
        <Route path="messages" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-dashboard", "parent-panel-view-announcements"]}
          >
            <ParentMessages />
          </RequireParentPermission>
        } />
        <Route path="certificates" element={
          <RequireParentPermission permission="parent-panel-view-certificates">
            <ParentCertificates />
          </RequireParentPermission>
        } />
        <Route path="add-student" element={
          <RequireParentPermission permission="parent-panel-manage-students">
            <AddStudent />
          </RequireParentPermission>
        } />
        <Route path="edit-student/:studentId" element={
          <RequireParentPermission permission="parent-panel-manage-students">
            <EditStudent />
          </RequireParentPermission>
        } />
        <Route path="my-student-list" element={
          <RequireParentPermission
            anyOf={["parent-panel-view-my-students", "parent-panel-manage-students"]}
          >
            <MyStudentList />
          </RequireParentPermission>
        } />
        <Route path="change-password" element={
          <RequireParentPermission permission="parent-panel-change-student-password">
            <ChangePassword />
          </RequireParentPermission>
        } />

        <Route path="billing" element={<SafeRedirect to={`/${process.env.REACT_APP_PARENT_PREFIX || 'parent'}/billing/all`} />} />
        <Route path="billing/all" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/course" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/mock" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/paper" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="billing/mobile" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        {/* Customer refund requests. Billing shows the Refunds tab only when the backend's
            REFUND_CUSTOMER_REQUESTS_ENABLED is on; otherwise this URL falls back to "All". */}
        <Route path="billing/refunds" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <Billing />
          </RequireParentPermission>
        } />
        <Route path="my-purchases" element={
          <RequireParentPermission permission="parent-panel-view-billing">
            <ParentMyPurchases />
          </RequireParentPermission>
        } />
        <Route path="billing-information" element={
          <RequireParentPermission permission="parent-panel-manage-billing-information">
            <BillingInformation />
          </RequireParentPermission>
        } />

        <Route path="end-of-report" element={
          <RequireParentPermission permission="parent-panel-view-end-of-report">
            <EndOfReport />
          </RequireParentPermission>
        } />
        <Route path="test-scores" element={
          <RequireParentPermission permission="parent-panel-view-test-scores">
            <TestScores />
          </RequireParentPermission>
        } />
        <Route path="finished-test" element={
          <RequireParentPermission permission="parent-panel-view-finished-test">
            <FinishedTest />
          </RequireParentPermission>
        } />
        <Route path="course-target-area" element={
          <RequireParentPermission permission="parent-panel-view-course-target-area">
            <CourseTargetArea />
          </RequireParentPermission>
        } />
        <Route path="ai-answer-sheet" element={
          <RequireParentPermission permission="parent-panel-view-ai-answer-sheet">
            <AiAnswerSheetUpload />
          </RequireParentPermission>
        } />
        <Route path="work-sheet/:id/take" element={
          <RequireParentPermission permission="parent-panel-view-ai-answer-sheet">
            <WorkSheetTake />
          </RequireParentPermission>
        } />
        <Route path="work-sheet/:testId/attempt/:attemptId/review" element={<WorkSheetReview />} />
        <Route path="work-sheet/:testId/sweep-history" element={<WorkSheetSweepHistory />} />

        <Route path="mock-exam-results" element={<ParentMockExamResults />} />
        <Route path="past-paper-results" element={<ParentPastPaperResults />} />
        <Route path="assistant-chats" element={<ChildAssistantChats />} />
        <Route path="classes" element={<ChildClasses />} />
        <Route path="mock-exam/result/:attemptId/report" element={<StudentMockExamReport />} />
        <Route path="mock-exam/result/:attemptId/review" element={<StudentMockExamReview />} />
        <Route path="past-paper/:purchaseId/result" element={<StudentPaperResult />} />
        <Route path="past-paper/:purchaseId/review" element={<StudentPaperReview />} />

        <Route path="settings" element={
          <RequireParentPermission
            permission="parent-panel-account-settings"
            allowBypassWhenMustChangePassword
          >
            <Setting />
          </RequireParentPermission>
        } />
      </Route>

      {/* TUTOR ROUTES (tutor panel; tutors use /tutor-login only) */}
      <Route path="/tutor" element={<RequireTutor><Outlet /></RequireTutor>}>
        {/* Meeting only - no sidebar/app bar; opens in new tab */}
        <Route path="meeting/:roomCode" element={
          <RequireTutorPermission permission="tutor-portal-classes">
            <TutorMeetingPage />
          </RequireTutorPermission>
        } />
        {/* All other tutor pages use TutorLayout */}
        <Route element={<TutorLayout />}>
          <Route index element={<TutorHomeGate />} />
          <Route path="students" element={
            <RequireTutorPermission permission="tutor-portal-students">
              <TutorStudents />
            </RequireTutorPermission>
          } />
          <Route path="students/:buyerId" element={
            <RequireTutorPermission permission="tutor-portal-students">
              <TutorStudentResources />
            </RequireTutorPermission>
          } />
          <Route path="students/:buyerId/review/:resourceKind/:reviewId" element={
            <RequireTutorPermission permission="tutor-portal-students">
              <TutorStudentResourceReview />
            </RequireTutorPermission>
          } />
          <Route path="courses" element={
            <RequireTutorPermission permission="tutor-portal-courses">
              <TutorCourses />
            </RequireTutorPermission>
          } />
          <Route path="groups" element={
            <RequireTutorPermission permission="tutor-portal-groups">
              <TutorGroups />
            </RequireTutorPermission>
          } />
          <Route path="resources" element={
            <RequireTutorPermission permission="tutor-portal-resources">
              <TutorResources />
            </RequireTutorPermission>
          } />
          <Route path="classes" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorClasses />
            </RequireTutorPermission>
          } />
          <Route path="classes/schedule" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorScheduleClass />
            </RequireTutorPermission>
          } />
          <Route path="recordings" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorRecordings />
            </RequireTutorPermission>
          } />
          <Route path="course/:courseId/class" element={
            <RequireTutorPermission permission="tutor-portal-classes">
              <TutorStartClass />
            </RequireTutorPermission>
          } />
          <Route path="mock-exams" element={
            <RequireTutorPermission permission="tutor-portal-mock-exams">
              <TutorMockExams />
            </RequireTutorPermission>
          } />
          <Route path="papers" element={
            <RequireTutorPermission permission="tutor-portal-papers">
              <TutorPapers />
            </RequireTutorPermission>
          } />
          <Route path="timeslots" element={
            <RequireTutorPermission permission="tutor-portal-timeslots">
              <TutorTimeslots />
            </RequireTutorPermission>
          } />
          <Route path="assignments" element={
            <RequireTutorPermission permission="tutor-portal-assignments">
              <TutorAssignments />
            </RequireTutorPermission>
          } />
          <Route path="tests" element={
            <RequireTutorPermission permission="tutor-portal-tests">
              <TutorTests />
            </RequireTutorPermission>
          } />
          <Route path="notifications" element={
            <RequireTutorPermission permission="tutor-portal-view-dashboard">
              <TutorNotifications />
            </RequireTutorPermission>
          } />
          <Route path="announcements" element={
            <RequireTutorPermission permission="tutor-portal-announcements">
              <TutorAnnouncements />
            </RequireTutorPermission>
          } />
          <Route path="awards" element={
            <RequireTutorPermission permission="tutor-portal-awards">
              <TutorAwards />
            </RequireTutorPermission>
          } />
          <Route path="certificates" element={
            <RequireTutorPermission permission="tutor-portal-certificates">
              <TutorCertificates />
            </RequireTutorPermission>
          } />
        </Route>
      </Route>

      {/* ROOT ROUTE - REDIRECT BY ROLE */}
      <Route path="/" element={<RedirectByRole />} />

      {/* GENERAL AUTH ROUTES (DEFAULT LAYOUT) - FOR BACKWARD COMPATIBILITY */}
      <Route path="/general" element={
        <RequireAuth>
          <Layout />
        </RequireAuth>
      }>
        <Route index element={<Dashboard />} />
        <Route path="student-pannel" element={<StudentPannel />} />
        <Route path="videos" element={<VideoLessonsPage />} />
        <Route path="my-course-video-page" element={<MyCourseVideoPage />} />
      </Route>

      {/* FALLBACK - 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
};

export default AppRoutes;
